import { useUserIdWithLoading } from '@dreamstack/auth'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { ComponentProps, FunctionComponent } from 'react'
import { cn } from '@dreamstack/util/src/cn'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { ProjectViewCounterAndButton } from './ProjectViewCounterAndButton'
import { useComingSoonBaseUrl } from './helper/useComingSoonBaseUrl'

const infoTitleClass =
  'font-semibold text-px-16 leading-px-24 lg:text-px-32 lg:leading-px-48'
const infoDescriptionClass =
  'text-px-13 leading-px-18 lg:text-px-16 lg:leading-px-24'

const InfoTitle = ({
  className,
  ...rest
}: ComponentProps<'div'>) => (
  <div className={cn(infoTitleClass, className)} {...rest} />
)

const InfoDescription = ({
  className,
  ...rest
}: ComponentProps<'div'>) => (
  <div className={cn(infoDescriptionClass, className)} {...rest} />
)

const useGetProjectSubtitle = (project: {
  city: string | null
  region: string | null
  comingSoonDate: string | null
}): string => {
  const t = useTranslation()
  const { city, region, comingSoonDate } = project
  return `${city}, ${region} - ${t(
    'accentro:comingSoon.salesStart'
  )} ${comingSoonDate}`
}

export type ComingSoonPreviewArgs = {
  id: number
  title: string | null
  city: string | null
  region: string | null
  comingSoonDate: string | null
  titleImageUrl: string | null
}

export const ComingSoonPreview: FunctionComponent<
  React.PropsWithChildren<{ project: ComingSoonPreviewArgs }>
> = ({ project }) => {
  const t = useTranslation()
  const locale = useLocale()
  const comingSoonBaseUrl = useComingSoonBaseUrl()
  const { userId } = useUserIdWithLoading()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  const comingSoonSubtitleTranslated = useGetProjectSubtitle(project)
  const comingSoonSubtitle = project.comingSoonDate
    ? comingSoonSubtitleTranslated
    : ''

  const projectSubtitle = comingSoonSubtitle

  const trackClickCallToActionButton = async () => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Coming Soon Projekt',
        url: router.asPath,
        additionalInfo: {
          projectId: project.id,
        },
      },
    })
  }

  const ctaButton = (
    <SimpleMuiButton
      color="inherit"
      endIcon={
        <ArrowRight className="text-accentroWhite-full h-px-24 w-px-24 lg:h-px-48 lg:w-px-48" />
      }
    >
      {t('accentro:projectNewsletterRegistration.registerNow')}
    </SimpleMuiButton>
  )

  const filteredHref = `${comingSoonBaseUrl}/${project.id}`
  return (
    <div>
      <Link
        onClick={trackClickCallToActionButton}
        onKeyDown={trackClickCallToActionButton} // Add keyboard event
        role="button" // Add role attribute
        tabIndex={0} // Make it focusable
        href={filteredHref}
        passHref
        data-cy={'ProjectPreviewLinkToComingSoonProject'}
      >
        <div className="aspect-[3/2] relative self-center cursor-pointer">
          <Image
            className="object-cover"
            fill
            src={
              project.titleImageUrl ??
              `/static/images/image-placeholder-small${locale.toUpperCase()}.png`
            }
            alt={'Project title image'}
          />
          {!!project.comingSoonDate && (
            <div className="h-full w-full bg-accentroGray-500 flex justify-center items-center">
              <div className="font-semibold text-center text-accentroWhite-full text-px-28">
                {project.comingSoonDate}
              </div>
            </div>
          )}
        </div>
        <div
          className={cn(
            'cursor-pointer bg-accentroGray-full text-accentroWhite-full relative p-px-16 mr-px-48',
            'md:px-px-24 md:py-px-32 md:ml-px-24 md:mr-px-40 md:-mt-px-48',
            'lg:px-px-48 lg:ml-px-48 lg:mr-px-80 lg:-mt-px-96'
          )}
        >
          <InfoTitle className="pr-px-48 md:pr-px-64">{project.title}</InfoTitle>
          <InfoDescription className="mb-px-24">
            {projectSubtitle}
          </InfoDescription>
          <>
            <ProjectViewCounterAndButton ctaButton={ctaButton} />
          </>
        </div>
      </Link>
    </div>
  )
}
